import React from 'react';
import { Card } from 'antd';
import { SkBar, SkChartCard } from './_primitives';

const StatCard = () => (
  <Card className="shadow-sm" styles={{ body: { padding: 20 } }}>
    <div className="flex items-start justify-between gap-3 mb-3">
      <div className="flex-1 min-w-0 flex flex-col gap-2">
        <SkBar h="h-3" w="w-24" />
        <SkBar h="h-7" w="w-28" />
      </div>
      <div className="w-10 h-10 rounded-lg bg-gray-200 animate-pulse flex-shrink-0" />
    </div>
    <div className="flex items-center gap-2">
      <SkBar h="h-3" w="w-4" />
      <SkBar h="h-3" w="w-32" />
    </div>
  </Card>
);

const QuickActionsCard = () => (
  <Card className="shadow-sm" styles={{ body: { padding: 20 } }}>
    <SkBar h="h-4" w="w-28" className="mb-4" />
    <div className="grid grid-cols-2 gap-3">
      {Array.from({ length: 4 }).map((_, i) => (
        <SkBar key={i} h="h-10" w="w-full" className="rounded-md" />
      ))}
    </div>
  </Card>
);

const TopBranchesCard = () => (
  <Card className="shadow-sm" styles={{ body: { padding: 20 } }}>
    <SkBar h="h-4" w="w-40" className="mb-4" />
    {Array.from({ length: 4 }).map((_, i) => (
      <div
        key={i}
        className="flex items-center gap-3 py-3 border-b border-gray-100 last:border-b-0"
      >
        <div className="w-8 h-8 rounded-full bg-gray-200 animate-pulse flex-shrink-0" />
        <div className="flex-1 min-w-0 flex flex-col gap-1.5">
          <SkBar h="h-3" w="w-24" />
          <SkBar h="h-2" w="w-32" />
        </div>
        <SkBar h="h-5" w="w-10" className="rounded-md flex-shrink-0" />
      </div>
    ))}
  </Card>
);

const RecentActivityCard = () => (
  <Card className="shadow-sm h-full" styles={{ body: { padding: 20 } }}>
    <div className="flex items-center justify-between mb-4">
      <SkBar h="h-4" w="w-32" />
      <SkBar h="h-5" w="w-12" className="rounded-md" />
    </div>
    {Array.from({ length: 5 }).map((_, i) => (
      <div
        key={i}
        className="flex items-center gap-3 py-3 border-b border-gray-100 last:border-b-0"
      >
        <div className="w-9 h-9 rounded-full bg-gray-200 animate-pulse flex-shrink-0" />
        <div className="flex-1 min-w-0 flex flex-col gap-1.5">
          <SkBar h="h-3" w="w-32" />
          <SkBar h="h-2" w="w-48" />
        </div>
      </div>
    ))}
  </Card>
);

const DashboardHeader = () => (
  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3 mb-4 sm:mb-6">
    <div className="flex flex-col gap-2 min-w-0 flex-1">
      <SkBar h="h-3" w="w-20" />
      <SkBar h="h-7" w="w-64" className="max-w-full" />
      <SkBar h="h-3" w="w-80" className="max-w-full" />
    </div>
    <SkBar h="h-9" w="w-28" className="rounded-md flex-shrink-0" />
  </div>
);

/**
 * DashboardPageLoader — matches Dashboard Overview layout:
 *  header · 6 stat cards (3×2) · points-flow + tier-distribution (2:1) ·
 *  user-growth + branch-performance (1:1) ·
 *  recent-activity + [quick-actions / top-branches] (1:1).
 */
const DashboardPageLoader = () => (
  <div className="w-full p-3 sm:p-4 md:p-6">
    <DashboardHeader />

    {/* 6 stat cards · 3 cols × 2 rows on lg */}
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 mb-4 sm:mb-6">
      {Array.from({ length: 6 }).map((_, i) => (
        <StatCard key={i} />
      ))}
    </div>

    {/* Points Flow (2/3) + Tier Distribution (1/3) */}
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 sm:gap-4 mb-4 sm:mb-6">
      <div className="lg:col-span-2">
        <SkChartCard heightClass="h-60 sm:h-64 md:h-72" />
      </div>
      <SkChartCard heightClass="h-60 sm:h-64 md:h-72" />
    </div>

    {/* User Growth + Branch Performance */}
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4 mb-4 sm:mb-6">
      <SkChartCard heightClass="h-52 sm:h-56 md:h-60" />
      <SkChartCard heightClass="h-52 sm:h-56 md:h-60" />
    </div>

    {/* Recent Activity + (Quick Actions stacked over Top Branches) */}
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4">
      <RecentActivityCard />
      <div className="flex flex-col gap-3 sm:gap-4">
        <QuickActionsCard />
        <TopBranchesCard />
      </div>
    </div>
  </div>
);

export default DashboardPageLoader;
